import { Alert, Button, Card, CardContent, CardHeader, Input, ScrollArea } from '@ibsys/kit';
import { FC, useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SaveIcon } from 'lucide-react';
import {
  useExpertReportWithVersion,
  useDeleteExpertReport,
  useUpdateExpertReport,
  flattenBlocks,
} from '@/entities/expert-report';
import { ExpertReportVersionDropdown } from '@/features/expert-report/ExpertReportVersionDropdown/ExpertReportVersionDropdown';
import { PointsRuleInput, type PointsRuleValue } from '@/features/expert-report/PointsRuleInput/PointsRuleInput';
import { PredicateInput } from '@/features/PredicateInput';
import { useResultsRecalculationNotice } from '@/features/expert-report/useResultsRecalculationNotice';
import { hasPredicateIssues, type PredicateValue } from '@/entities/predicate';
import { useHasPermission } from '@/entities/profile';
import { ConfirmationButton } from '@/shared/ui';

type ExpertReportCardProps = {
  reportId: number;
  onDeleted: () => void;
  className?: string;
};

/** Правимые поля карточки заключения */
type ExpertReportCardFormValues = {
  name: string;
  /** Ограничение — предикат применимости заключения */
  predicate: PredicateValue;
  /** Правило расчёта итога заключения по блокам верхнего уровня */
  pointsRule: PointsRuleValue;
  /** Размерность итога заключения; пустая строка — размерности нет */
  unit: string;
};

const EMPTY_FORM: ExpertReportCardFormValues = {
  name: '',
  predicate: { dsl: '', dslHuman: '' },
  pointsRule: { dsl: '', dslHuman: '' },
  unit: '',
};

export const ExpertReportCard: FC<ExpertReportCardProps> = ({ reportId, onDeleted, className }) => {
  const { t } = useTranslation(['common', 'expertReport']);
  const {
    selectedReport: report,
    baseReport,
    isLoading,
    isLastVersion,
    versionId,
    setVersion,
  } = useExpertReportWithVersion(reportId);
  const { mutateAsync: deleteReport, isPending: isDeleting } = useDeleteExpertReport();
  const { mutateAsync: updateReport, isPending: isUpdating } = useUpdateExpertReport();
  const allowAdmin = useHasPermission('ADMIN_EXPERT_REPORT');
  const noticeResultsRecalculation = useResultsRecalculationNotice();
  // Редактирование доступно только у последней версии и при праве администрирования
  const canEdit = isLastVersion && allowAdmin;
  const [form, setForm] = useState<ExpertReportCardFormValues>(EMPTY_FORM);
  const [hasChanges, setHasChanges] = useState(false);
  const { name, predicate, pointsRule, unit } = form;
  // Пока условие непригодно, заключение с ним не сохраняется
  const hasIssues = hasPredicateIssues(predicate);

  useEffect(() => {
    if (report) {
      setForm((prev) => ({
        ...prev,
        name: report.name,
        predicate: {
          dsl: report.predicate?.dsl ?? '',
          dslHuman: report.predicate?.dslHuman ?? '',
        },
      }));
      setHasChanges(false);
    }
  }, [report]);

  // Правило расчёта и размерность итога хранятся у заключения, а не у версии, поэтому берутся
  // из текущего состояния, а не из просматриваемого снимка
  useEffect(() => {
    if (baseReport) {
      setForm((prev) => ({
        ...prev,
        pointsRule: {
          dsl: baseReport.pointsRule?.dsl ?? '',
          dslHuman: baseReport.pointsRule?.dslHuman ?? '',
        },
        unit: baseReport.unit ?? '',
      }));
    }
  }, [baseReport]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, name: e.target.value }));
    setHasChanges(e.target.value !== report?.name);
  };

  const handlePredicateChange = useCallback((value: PredicateValue) => {
    setForm((prev) => ({ ...prev, predicate: value }));
    setHasChanges(true);
  }, []);

  const handlePointsRuleChange = useCallback((value: PointsRuleValue) => {
    setForm((prev) => ({ ...prev, pointsRule: value }));
    setHasChanges(true);
  }, []);

  const handleUnitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, unit: e.target.value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!baseReport || !name.trim() || hasIssues) return;
    const predicateInput = predicate.dsl ? { dsl: predicate.dsl, dslHuman: predicate.dslHuman } : undefined;
    const updatedReport = await updateReport({
      id: reportId,
      body: {
        name: name.trim(),
        predicate: predicateInput,
        pointsRule,
        // Размерность итога уходит вместе с остальными полями: пустое значение её снимает
        unit: unit.trim(),
        blocks: flattenBlocks(baseReport.blocks),
      },
    });
    setHasChanges(false);
    noticeResultsRecalculation(updatedReport);
  };

  const handleDelete = async () => {
    await deleteReport(reportId);
    onDeleted();
  };

  if (isLoading) {
    return (
      <Card className="w-full h-fit">
        <CardHeader>
          <div className="text-muted-foreground">{t('common:loading')}</div>
        </CardHeader>
      </Card>
    );
  }

  if (!report) {
    return (
      <Card className="w-full h-fit">
        <CardHeader>
          <div className="text-muted-foreground">{t('expertReport:selectItem')}</div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="space-y-4">
          {baseReport?.resultsRecalculating && <Alert type="info" title={t('expertReport:resultsRecalculating')} />}

          <ExpertReportVersionDropdown reportId={reportId} value={versionId} onChange={setVersion} />

          <Input
            id="report-name"
            label={t('expertReport:reportName')}
            value={name}
            onChange={handleNameChange}
            disabled={!canEdit}
          />

          <PredicateInput
            label={t('expertReport:predicate')}
            type="textarea"
            value={predicate}
            onChange={handlePredicateChange}
            disabled={!canEdit}
          />

          <PointsRuleInput
            value={pointsRule}
            onChange={handlePointsRuleChange}
            issues={baseReport?.pointsRule?.issues}
            disabled={!canEdit}
            isProcessing={isUpdating}
          />

          <Input
            id="report-unit"
            label={t('expertReport:unit')}
            placeholder={t('expertReport:unitPlaceholder')}
            value={unit}
            onChange={handleUnitChange}
            disabled={!canEdit}
          />
        </div>
      </CardHeader>
      <CardContent className={'min-h-0'}>
        <ScrollArea>
          <div className="flex items-center gap-2">
            {canEdit && hasChanges && (
              <Button
                variant="default"
                onClick={handleSave}
                disabled={isUpdating || !name.trim() || hasIssues}
                icon={<SaveIcon className="size-4" />}
              >
                {t('common:save')}
              </Button>
            )}

            {canEdit && (
              <ConfirmationButton
                onConfirm={handleDelete}
                isLoadingConfirm={isDeleting}
                title={t('expertReport:removeReport')}
                description={
                  <Trans
                    i18nKey="expertReport:removeReportDescription"
                    values={{ reportName: report.name }}
                    components={{ strong: <strong /> }}
                  />
                }
                trigger={{
                  variant: 'outline',
                  color: 'destructive',
                  children: t('expertReport:deleteReport'),
                }}
              />
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};
